import { AnalyticsEvent, AnalyticsMetrics } from "../../types/Analytics";

export const calculateMetrics = (events: AnalyticsEvent[]): AnalyticsMetrics => {
  const uniqueUsers = new Set<string>();
  const eventsByType: Record<string, number> = {};
  const usersByPlatform: Record<string, number> = {};
  const signupsByProvider: Record<string, number> = {};

  events.forEach((event) => {
    // Track unique users
    uniqueUsers.add(event.userId);

    // Count events by type
    eventsByType[event.event] = (eventsByType[event.event] || 0) + 1;

    // Count users by platform
    usersByPlatform[event.platform] = (usersByPlatform[event.platform] || 0) + 1;

    // Count signups by provider
    if (event.event === "signup_success" && event.provider) {
      signupsByProvider[event.provider] = (signupsByProvider[event.provider] || 0) + 1;
    }
  });

  const totalUsers = uniqueUsers.size;
  const newUsers = eventsByType["signup_success"] || 0;

  return {
    totalUsers,
    activeUsers: totalUsers, // For now, consider all users as active
    newUsers,
    totalEvents: events.length,
    eventsByType,
    usersByPlatform,
    signupsByProvider,
  };
};

export const getActiveUsersInDateRange = (
  events: AnalyticsEvent[],
  startDate: Date,
  endDate: Date
): number => {
  const startTs = startDate.getTime();
  const endTs = endDate.getTime();

  const activeUsers = new Set<string>();

  events.forEach((event) => {
    if (event.ts >= startTs && event.ts <= endTs) {
      activeUsers.add(event.userId);
    }
  });

  return activeUsers.size;
};

// Paying-only variants for hard-paywall communities (config.subRequired).
// `subscribedIds` is the set of member userIds with an active subscription
// (Community/{id}/Members where isSubscribed === true). The raw active-user
// counts above include visitors stuck at the paywall who never subscribed;
// these count only users who both fired an event and are subscribed.
export const countPayingUsers = (
  events: AnalyticsEvent[],
  subscribedIds: Set<string>
): number => {
  const users = new Set<string>();
  events.forEach((event) => {
    if (event.userId && subscribedIds.has(event.userId)) {
      users.add(event.userId);
    }
  });
  return users.size;
};

export const getPayingActiveUsersInDateRange = (
  events: AnalyticsEvent[],
  startDate: Date,
  endDate: Date,
  subscribedIds: Set<string>
): number => {
  const startTs = startDate.getTime();
  const endTs = endDate.getTime();

  const activeUsers = new Set<string>();

  events.forEach((event) => {
    if (
      event.ts >= startTs &&
      event.ts <= endTs &&
      event.userId &&
      subscribedIds.has(event.userId)
    ) {
      activeUsers.add(event.userId);
    }
  });

  return activeUsers.size;
};

export const getNewUsersInDateRange = (
  events: AnalyticsEvent[],
  startDate: Date,
  endDate: Date
): number => {
  const startTs = startDate.getTime();
  const endTs = endDate.getTime();

  const newUsers = events.filter(
    (event) =>
      event.event === "signup_success" &&
      event.ts >= startTs &&
      event.ts <= endTs
  );

  return newUsers.length;
};

export const getTopEvents = (
  events: AnalyticsEvent[],
  limit: number = 10
): Array<{ event: string; count: number }> => {
  const eventCounts: Record<string, number> = {};

  events.forEach((event) => {
    eventCounts[event.event] = (eventCounts[event.event] || 0) + 1;
  });

  return Object.entries(eventCounts)
    .map(([event, count]) => ({ event, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};

export const getUsersByPlatform = (
  events: AnalyticsEvent[]
): Record<string, number> => {
  const platformCounts: Record<string, number> = {};

  events.forEach((event) => {
    platformCounts[event.platform] = (platformCounts[event.platform] || 0) + 1;
  });

  return platformCounts;
};

export const formatTimestamp = (ts: number): string => {
  const date = new Date(ts);
  return date.toLocaleString();
};

export const getEventTrend = (
  events: AnalyticsEvent[],
  days: number = 7
): Array<{ date: string; count: number }> => {
  const now = new Date();
  const trend: Array<{ date: string; count: number }> = [];

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    date.setHours(0, 0, 0, 0);

    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + 1);

    const count = events.filter(
      (event) => event.ts >= date.getTime() && event.ts < nextDate.getTime()
    ).length;

    trend.push({
      date: date.toLocaleDateString(),
      count,
    });
  }

  return trend;
};
